import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getVacancyBookingsList, getVacancyList } from 'logic/store/elitetutor/elitetutor.selectors';
import { elitetutorSlice } from 'logic/store/elitetutor/elitetutor.slice';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import {
  CurrentWeekSpan,
  DaySpan,
  HeaderButtons,
  STable,
  STd,
  STr,
  StyledButton,
  StyledTbody,
  StyledThead,
  VacancyReminderModalSpan,
} from './TutoringDateTimePicker.style';
import { SlotState, TimeSlot, ViewAs } from './TutoringDateTimePicker.types';
import {
  countSelectedTimeSlots,
  initializeWeek,
  isPrimeday,
  isPrimetime,
  mapSelectedTimeslotsToVacanciesRequest,
  PICKABLE_START_TIME,
  POSSIBLE_MOBILE_PICK_DURATION_DAYS,
  POSSIBLE_PICK_DURATION_DAYS,
  POSSIBLE_TIME_SLOTS,
  PRIMETIME_START,
  roundToNextSlot,
  TIME_SLOT_DURATION_IN_MINUTES,
  VACANCY_SLOT_IGNORE_REMINDER_THRESHOLD,
  VACANCY_SLOT_MOBILE_IGNORE_REMINDER_THRESHOLD,
  validateSelectedTimeSlots,
  WEEKSTART_OFFSET_IN_MINUTES,
} from './TutoringDateTimePicker.logic';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { isAdmin } from 'logic/store/authentication/authentication.selectors';
import { TimeSlotItem } from 'components/TutoringDateTimePicker/components/TimeSlotItem';
import { Modal } from 'antd';
import { Text } from 'components/text/Text';

type Props = {
  bookingListener?: (booking: Date[]) => void;
  tutorID?: string;
  viewAs: ViewAs;
  reloadState?: any;
  weekstartOffsetInMinutes?: number;
};

const TutoringDateTimePicker: React.FC<Props> = ({
  bookingListener,
  tutorID,
  viewAs,
  reloadState,
  weekstartOffsetInMinutes = WEEKSTART_OFFSET_IN_MINUTES,
}) => {
  const _isAdmin = useSelector(isAdmin);
  const vacancyList = useSelector(getVacancyList);
  const bookedList = useSelector(getVacancyBookingsList);
  const { maxMobileLarge } = useBreakpoints();
  const possibleTimeDuration = maxMobileLarge
    ? POSSIBLE_MOBILE_PICK_DURATION_DAYS
    : POSSIBLE_PICK_DURATION_DAYS;
  const vacancySlotIgnoreReminderThreshold = maxMobileLarge
    ? VACANCY_SLOT_MOBILE_IGNORE_REMINDER_THRESHOLD
    : VACANCY_SLOT_IGNORE_REMINDER_THRESHOLD;
  const [disableStartRestriction, setDisableStartRestriction] = useState(false);
  const [vacancyReminderModal, setVacancyReminderModal] = useState<
    'previous' | 'next' | 'save' | null
  >(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const dispatch = useDispatch();
  const [currentDate, setCurrentDate] = useState<moment.Moment>(moment());
  const currentOffsetTime = roundToNextSlot(currentDate).add(weekstartOffsetInMinutes, 'minutes');
  const { initSelectedTimeSlots, currentWeekDayList } = initializeWeek(
    currentDate,
    vacancyList,
    possibleTimeDuration,
    bookedList,
    viewAs,
    currentOffsetTime,
    disableStartRestriction
  );
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<TimeSlot[]>(initSelectedTimeSlots);
  const [bookingList, setBookingList] = useState<Date[]>([]);
  const pickableTimeList: moment.Moment[] = [];

  //initpicker
  const tempTime = moment(PICKABLE_START_TIME, 'hh:mm');
  for (let k = 0; k < POSSIBLE_TIME_SLOTS; k++) {
    pickableTimeList.push(tempTime.clone());
    tempTime.add(TIME_SLOT_DURATION_IN_MINUTES, 'm');
  }
  //end of initpicker

  useEffect(() => {
    dispatch(elitetutorSlice.actions.fetchVacancyList({ tutorID: tutorID || '' }));
    //workaround to enable always scroll to primetime
    setTimeout(() => document?.getElementById(PRIMETIME_START)?.scrollIntoView(), 0);
    setBookingList([]);
    if (bookingListener) bookingListener([]);
  }, [reloadState]);

  useEffect(() => {
    const { initSelectedTimeSlots } = initializeWeek(
      currentDate,
      vacancyList,
      possibleTimeDuration,
      bookedList,
      viewAs,
      currentOffsetTime,
      disableStartRestriction
    );
    setSelectedTimeSlots(initSelectedTimeSlots);
  }, [currentDate, vacancyList, disableStartRestriction, maxMobileLarge]);

  const resetSlotBooking = (selectedTimeSlots: TimeSlot[]) => {
    for (let i = 0; i < selectedTimeSlots.length; ++i) {
      for (let j = 0; j < selectedTimeSlots[i].slots.length; ++j) {
        if (selectedTimeSlots[i].slots[j] === SlotState.BOOKED) {
          selectedTimeSlots[i].slots[j] = SlotState.AVAILABLE;
        }
      }
    }
  };

  const toggleTimeSlots = (timeSlot: moment.Moment) => {
    //is in selectedTimeSlots
    const diffInMinutes = timeSlot.diff(timeSlot.clone().startOf('day'), 'minutes');
    const diffInTimeSlots =
      diffInMinutes % TIME_SLOT_DURATION_IN_MINUTES === 0
        ? diffInMinutes / TIME_SLOT_DURATION_IN_MINUTES
        : null;

    const indexOfDayOfWeek = selectedTimeSlots.findIndex(
      (_timeSlot) => _timeSlot.date === timeSlot.format('YYYY-MM-DD')
    ); //index of day of week

    if (indexOfDayOfWeek > -1) {
      if (diffInTimeSlots) {
        const slotState = selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots];
        if (viewAs === ViewAs.ELITE_TUTOR) {
          setUnsavedChanges(true);
          switch (slotState) {
            case SlotState.AVAILABLE:
              selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots] = SlotState.OPEN;
              break;
            case SlotState.OPEN:
              selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots] = SlotState.AVAILABLE;
              break;
          }
        }
        if (viewAs === ViewAs.ELITE_SEEKER) {
          switch (slotState) {
            case SlotState.AVAILABLE:
              resetSlotBooking(selectedTimeSlots);
              //check if next timeslot is also available only works if only 1 slot selectable
              if (
                selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots + 1] !==
                SlotState.AVAILABLE
              )
                return;

              selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots] = SlotState.BOOKED;
              bookingList.splice(0, bookingList.length);
              bookingList.push(new Date(timeSlot.format()));
              setBookingList([...bookingList]);
              if (bookingListener) bookingListener(bookingList);
              break;
            case SlotState.BOOKED:
              bookingList.splice(
                bookingList.findIndex((booking) => booking === new Date(timeSlot.format())),
                1
              );
              setBookingList([...bookingList]);
              selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots] = SlotState.AVAILABLE;
              if (bookingListener) bookingListener(bookingList);
              break;
          }
        }
        if (viewAs === ViewAs.ELITE_TUTOR_BOOKING) {
          switch (slotState) {
            case SlotState.OPEN:
            case SlotState.AVAILABLE:
              resetSlotBooking(selectedTimeSlots);
              //check if next timeslot is also available only works if only 1 slot selectable
              if (
                selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots + 1] ===
                SlotState.UNAVAILABLE
              )
                return;

              selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots] = SlotState.BOOKED;
              bookingList.splice(0, bookingList.length);
              bookingList.push(new Date(timeSlot.format()));
              setBookingList([...bookingList]);
              if (bookingListener) bookingListener(bookingList);
              break;
            case SlotState.BOOKED:
              bookingList.splice(
                bookingList.findIndex((booking) => booking === new Date(timeSlot.format())),
                1
              );
              setBookingList([...bookingList]);
              selectedTimeSlots[indexOfDayOfWeek].slots[diffInTimeSlots] = SlotState.AVAILABLE;
              if (bookingListener) bookingListener(bookingList);
              break;
          }
        }
      }
      setSelectedTimeSlots([...selectedTimeSlots]);
    }
  };

  const isTimeSlotSelected = (timeSlot: moment.Moment): SlotState => {
    const diffOfDayInMinutes = timeSlot.diff(timeSlot.clone().startOf('day'), 'minutes');
    const diffOfDayInTimeSlots =
      diffOfDayInMinutes % TIME_SLOT_DURATION_IN_MINUTES === 0
        ? diffOfDayInMinutes / TIME_SLOT_DURATION_IN_MINUTES
        : null;
    const indexOfSelectedTimeSlotDay = selectedTimeSlots.findIndex(
      (_timeSlot) => _timeSlot.date === timeSlot.format('YYYY-MM-DD')
    ); //index of day of week

    if (indexOfSelectedTimeSlotDay === -1 || !diffOfDayInTimeSlots) return SlotState.OPEN;
    const currentSelectedTimeSlot =
      selectedTimeSlots[indexOfSelectedTimeSlotDay].slots[diffOfDayInTimeSlots];
    if (viewAs === ViewAs.ELITE_SEEKER && currentSelectedTimeSlot === SlotState.AVAILABLE) {
      return selectedTimeSlots[indexOfSelectedTimeSlotDay].slots[diffOfDayInTimeSlots + 1] ===
        SlotState.OPEN
        ? SlotState.OPEN
        : currentSelectedTimeSlot;
    }
    return currentSelectedTimeSlot;
  };

  const saveTimeSlots = () => {
    if (!validateSelectedTimeSlots(selectedTimeSlots)) return;
    setUnsavedChanges(false);
    const startTime =
      currentDate.diff(moment(), 'days') > 1 ? currentDate.startOf('day') : currentOffsetTime;
    dispatch(
      elitetutorSlice.actions.updateAvailability({
        updateElitetutorVacanciesRequest: mapSelectedTimeslotsToVacanciesRequest(
          selectedTimeSlots,
          possibleTimeDuration,
          startTime,
          disableStartRestriction
        ),
        tutorID: tutorID || '',
      })
    );
    setVacancyReminderModal(null);
  };

  const changeSelectionRange = (option: 'next' | 'previous') => {
    if (unsavedChanges && viewAs === ViewAs.ELITE_TUTOR) {
      if (!validateSelectedTimeSlots(selectedTimeSlots)) return;
      saveTimeSlots();
    }
    // Different implamentation as the backend; Backend uses 4 weeks in minutes
    if (
      option === 'next' &&
      currentDate.clone().add(possibleTimeDuration, 'days').unix() < moment().add(3, 'weeks').unix()
    ) {
      setBookingList([]);
      setSelectedTimeSlots([]);
      setCurrentDate(currentDate.clone().add(possibleTimeDuration, 'days'));
      if (bookingListener) bookingListener([]);
    }
    if (
      option === 'previous' &&
      currentDate.clone().subtract(possibleTimeDuration, 'days').unix() >=
        moment().clone().startOf('day').unix()
    ) {
      setBookingList([]);
      setSelectedTimeSlots([]);
      setCurrentDate(currentDate.clone().subtract(possibleTimeDuration, 'days'));
      if (bookingListener) bookingListener([]);
    }
    setVacancyReminderModal(null);
  };

  const renderTimeSlotPicker = () => {
    return pickableTimeList.map((pickableTime, i) => (
      <STr
        key={i}
        id={pickableTimeList[i].format('HH:mm')}
        primetime={viewAs === ViewAs.ELITE_TUTOR ? isPrimetime(pickableTimeList[i]) : false}
      >
        {currentWeekDayList.map((currentWeekDay, j) => {
          const timeSlot = moment(
            currentWeekDay.format('DD.MM.YYYY-') + pickableTimeList[i].format('HH:mm'),
            'DD.MM.YYYY-HH:mm'
          );
          return (
            <STd
              key={j}
              primetime={viewAs === ViewAs.ELITE_TUTOR ? isPrimeday(currentWeekDay) : false}
            >
              <TimeSlotItem
                slotState={isTimeSlotSelected(timeSlot)}
                onClick={() => toggleTimeSlots(timeSlot)}
                viewAs={viewAs}
              >
                {pickableTimeList[i].format('HH:mm')}
              </TimeSlotItem>
            </STd>
          );
        })}
      </STr>
    ));
  };

  const handleSaveTimeSlotClick = () => {
    if (!validateSelectedTimeSlots(selectedTimeSlots)) return;
    if (countSelectedTimeSlots(selectedTimeSlots) >= vacancySlotIgnoreReminderThreshold)
      return saveTimeSlots();
    setVacancyReminderModal('save');
  };

  const handleWeekChangeClick = (option: 'next' | 'previous') => {
    if (
      viewAs !== ViewAs.ELITE_TUTOR ||
      !unsavedChanges ||
      countSelectedTimeSlots(selectedTimeSlots) >= vacancySlotIgnoreReminderThreshold
    )
      return changeSelectionRange(option);
    if (unsavedChanges) setVacancyReminderModal(option);
  };

  const handleModalOk = () => {
    switch (vacancyReminderModal) {
      case 'next':
      case 'previous':
        changeSelectionRange(vacancyReminderModal);
        break;
      case 'save':
        saveTimeSlots();
    }
  };

  return (
    <>
      <HeaderButtons>
        <StyledButton onClick={() => handleWeekChangeClick('previous')}> &lt; </StyledButton>
        <StyledButton onClick={() => handleWeekChangeClick('next')}> &gt; </StyledButton>

        <CurrentWeekSpan>
          {!maxMobileLarge
            ? currentWeekDayList[0].format('DD MMM - ') +
              currentWeekDayList[currentWeekDayList.length - 1].format('DD MMM YYYY')
            : ''}
        </CurrentWeekSpan>
        {_isAdmin && (
          <StyledButton onClick={() => setDisableStartRestriction(!disableStartRestriction)}>
            {translate(translationKeys.timeSlotPicker.button.disableStartRestriction)}
          </StyledButton>
        )}
        {viewAs === ViewAs.ELITE_TUTOR && (
          <StyledButton style={{ marginLeft: 'auto' }} onClick={() => handleSaveTimeSlotClick()}>
            {translate(translationKeys.timeSlotPicker.button.save)}
          </StyledButton>
        )}
      </HeaderButtons>
      <STable>
        <StyledThead>
          <STr>
            {currentWeekDayList.map((currentWeekDay, i) => (
              <STd key={i}>
                {currentWeekDay.format('dd')}
                <DaySpan>{currentWeekDay.format('DD')}</DaySpan>
              </STd>
            ))}
          </STr>
        </StyledThead>
        <StyledTbody>{renderTimeSlotPicker()}</StyledTbody>
      </STable>
      <Modal
        title={''}
        visible={!!vacancyReminderModal}
        onOk={() => handleModalOk()}
        okText={translate(translationKeys.timeSlotPicker.button.save)}
        onCancel={() => setVacancyReminderModal(null)}
        cancelText={translate(translationKeys.buttons.abort)}
      >
        <VacancyReminderModalSpan>
          <Text fontWeight={600} size={18}>
            {translate(translationKeys.timeSlotPicker.vacancyReminder.title)}
          </Text>
        </VacancyReminderModalSpan>
        <VacancyReminderModalSpan>
          {translate(translationKeys.timeSlotPicker.vacancyReminder.info1, {
            recommendedHours:
              (vacancySlotIgnoreReminderThreshold * TIME_SLOT_DURATION_IN_MINUTES) / 60,
          })}
        </VacancyReminderModalSpan>
      </Modal>
    </>
  );
};

export default TutoringDateTimePicker;
